import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Shield, Lock, ExternalLink, Clock, Video, Users } from "lucide-react";

const MS_BOOKINGS_URL =
  "https://outlook.office.com/bookwithme/user/efddaaf235c647b6869642090f8b105e@eqhosensecapital.com?anonymous&ismsaljsauthenabled&ep=plink";

// Executive roster shown beneath the primary CTA. The unified MS Bookings
// page will let the prospect pick the right person inside Microsoft's flow.
const EXECUTIVES = [
  { initials: "DH", name: 'Donald "Donnie" Hensley Jr.', role: "Managing Director · Investor Solutions & Strategic Allocations", email: "accredited@echosensecapital.com" },
  { initials: "JJ", name: "Jeff Jenson",     role: "Co-CEO · Onboarding Interviews" },
];

const BookPage = () => {
  return (
    <div className="min-h-screen bg-obsidian relative" data-testid="book-page">
      {/* Top bar */}
      <div className="border-b border-gold/15 bg-obsidian/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-cream/70 hover:text-gold transition-colors"
            data-testid="book-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="text-sm tracking-widest uppercase">Return</span>
          </Link>
          <div className="flex items-center gap-2 text-gold">
            <Calendar className="w-5 h-5" />
            <span className="text-xs tracking-[0.25em] uppercase text-cream/60">
              EchoSense Capital Club · Private Booking
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-gold text-xs tracking-[0.35em] uppercase mb-4">
            Confidential Intake
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-cream mb-6">
            Schedule a Private Consultation
          </h1>
          <p className="text-cream/70 text-base max-w-2xl mx-auto leading-relaxed">
            Reserve a discreet, time-boxed session with a member of the
            EchoSense desk. Select the right executive for your inquiry inside
            our secure Microsoft 365 booking flow.
          </p>
        </div>

        {/* Primary CTA card */}
        <div className="border border-gold/30 bg-gradient-to-b from-obsidian to-black/80 rounded-lg p-10 text-center mb-10 shadow-2xl shadow-black/40">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gold/40 bg-obsidian mb-6">
            <Calendar className="w-7 h-7 text-gold" />
          </div>

          <h2 className="text-2xl font-serif text-cream mb-3">
            Open Your Private Calendar
          </h2>
          <p className="text-cream/60 text-sm max-w-md mx-auto mb-8 leading-relaxed">
            You will be taken to our secure Microsoft 365 booking page in a new
            tab. Select your executive and an available slot, then confirm with
            your contact details.
          </p>

          <a
            href={MS_BOOKINGS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold/90 text-navy-dark font-semibold tracking-widest uppercase text-sm px-10 py-4 rounded transition-all hover:shadow-lg hover:shadow-gold/20"
            data-testid="book-now-cta"
          >
            <Calendar className="w-4 h-4" />
            Book Your Consultation
            <ExternalLink className="w-4 h-4 opacity-70" />
          </a>

          <p className="text-cream/40 text-[10px] tracking-widest uppercase mt-6">
            Opens in a new window · Microsoft 365 Secured
          </p>
        </div>

        {/* Executive roster — gold monogram cards */}
        <div className="mb-12" data-testid="book-team-roster">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Users className="w-4 h-4 text-gold" />
            <p className="text-gold text-[11px] tracking-[0.35em] uppercase">
              Meet With Our Desk
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {EXECUTIVES.map((e) => (
              <div
                key={e.initials}
                className="flex flex-col items-center text-center p-5 border border-gold/15 bg-obsidian/50 rounded hover:border-gold/30 transition-colors"
                data-testid={`book-exec-${e.initials.toLowerCase()}`}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-3 border border-gold/40"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(234,179,8,0.18), rgba(234,179,8,0.04))",
                  }}
                >
                  <span className="text-gold font-serif text-xl tracking-widest">
                    {e.initials}
                  </span>
                </div>
                <p className="text-cream text-sm font-medium">{e.name}</p>
                <p className="text-cream/55 text-[11px] mt-1 leading-snug">{e.role}</p>
                {e.email && (
                  <a
                    href={`mailto:${e.email}`}
                    className="inline-block text-gold/80 hover:text-gold text-[11px] mt-2 tracking-wide underline underline-offset-2 transition-colors"
                    data-testid={`book-exec-${e.initials.toLowerCase()}-email`}
                  >
                    {e.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* What to expect */}
        <div className="grid md:grid-cols-3 gap-3 mb-10">
          <div className="flex items-start gap-3 p-5 border border-gold/15 bg-obsidian/60 rounded">
            <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-cream text-sm font-medium mb-1">30 Minutes</p>
              <p className="text-cream/55 text-xs leading-relaxed">
                Time-boxed sessions to respect your calendar.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-5 border border-gold/15 bg-obsidian/60 rounded">
            <Video className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-cream text-sm font-medium mb-1">
                Video or Phone
              </p>
              <p className="text-cream/55 text-xs leading-relaxed">
                Choose your preferred channel during booking.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-5 border border-gold/15 bg-obsidian/60 rounded">
            <Shield className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-cream text-sm font-medium mb-1">
                Introducer-Only
              </p>
              <p className="text-cream/55 text-xs leading-relaxed">
                No solicitation. No investment advice provided.
              </p>
            </div>
          </div>
        </div>

        {/* Compliance strip */}
        <div className="flex items-center justify-center gap-2 text-cream/40 text-xs">
          <Lock className="w-3.5 h-3.5" />
          <span>Encrypted &amp; hosted on Microsoft 365 · Your data stays inside our tenant.</span>
        </div>

        {/* Compliance footer */}
        <p className="text-center text-cream/40 text-[10px] tracking-widest uppercase mt-16">
          EchoSense Capital Club acts solely as an Introducer. No investment advice
          is provided.
        </p>
      </div>
    </div>
  );
};

export default BookPage;
